import { Button } from "@root/shared/components/Button";
import { type FC, useState } from "react";
import { Icon } from "@root/shared/components/Icon";
import { fontSize } from "@root/shared/styles/sprinkles.css";
import { PopOver } from "@root/shared/components/PopOver";
import {
  type TimeSlot,
  type InitialValue,
  type DateRange,
} from "@userFrontend/features/entry/types/preferredDayAndTimes";
import { PreferredDayAndTimeItem } from "./uiParts/PreferredDayAndTimeItem";
import * as styles from "./styles.css";

type Props = {
  availableTimeSlots: TimeSlot[];
  allDayValue: number;
  dateRange: DateRange;
  formNameBase: string;
  initialValues?: InitialValue[];
  errors?: Record<string, string[]>;
};

type Candidate = {
  id: number;
  selectedDate: Date | undefined;
  timeOption: string | undefined;
  selectedTimeSlots: number[];
};

const MAX_CANDIDATE_COUNT = 3;

const TIME_OPTION = {
  ALL_DAY: "allDay",
  TIME_RANGE: "timeRange",
} as const;

const TIME_TYPE_OPTIONS = [
  { label: "終日OK", value: TIME_OPTION.ALL_DAY },
  { label: "時間を指定する", value: TIME_OPTION.TIME_RANGE },
];

const EMPTY_STATE: Omit<Candidate, "id"> = {
  selectedDate: undefined,
  timeOption: undefined,
  selectedTimeSlots: [],
};

const parseInitialValue = (
  initialValue: InitialValue | undefined,
  allDayValue: number,
): Omit<Candidate, "id"> => {
  if (!initialValue) return EMPTY_STATE;

  const { date, timeSlots } = initialValue;

  // 日付が存在し、パース可能な形式（例: "YYYY-MM-DD"）の場合のみDateオブジェクトに変換
  const isValidDate = date && !Number.isNaN(Date.parse(date));
  const selectedDate = isValidDate ? new Date(date) : undefined;

  // 時間帯が未選択の場合は、日付のみ保持して時間オプションは未選択状態
  if (!timeSlots || timeSlots.length === 0) {
    return { ...EMPTY_STATE, selectedDate };
  }

  // 「終日OK」が選択されている場合（allDayValueのみが含まれる）
  if (timeSlots.length === 1 && timeSlots[0] === allDayValue) {
    return { selectedDate, timeOption: TIME_OPTION.ALL_DAY, selectedTimeSlots: [] };
  }

  // 「時間を指定する」が選択されている場合（具体的な時間帯が含まれる）
  return {
    selectedDate,
    timeOption: TIME_OPTION.TIME_RANGE,
    selectedTimeSlots: timeSlots.filter((slot) => slot !== allDayValue),
  };
};

export const PreferredDayAndTimes: FC<Props> = ({
  availableTimeSlots,
  allDayValue,
  dateRange,
  formNameBase,
  initialValues = [],
  errors = {},
}) => {
  const initialCount = Math.max(1, initialValues.length);
  const [nextId, setNextId] = useState(initialCount);
  const [candidates, setCandidates] = useState<Candidate[]>(() =>
    Array.from({ length: initialCount }, (_, i) => ({
      id: i,
      ...parseInitialValue(initialValues[i], allDayValue),
    })),
  );

  // 新しい候補を追加（最大3件まで）
  const handleAddCandidate = () => {
    if (candidates.length >= MAX_CANDIDATE_COUNT) return;
    setCandidates([...candidates, { id: nextId, ...EMPTY_STATE }]);
    setNextId(nextId + 1);
  };

  // 指定した候補を削除（最低1件は残す）
  const handleDeleteCandidate = (id: number) => {
    if (candidates.length <= 1) return;
    setCandidates(candidates.filter((c) => c.id !== id));
  };

  // 全候補を削除して第1候補のみの空状態に戻す
  const handleReset = () => {
    setCandidates([{ id: 0, ...EMPTY_STATE }]);
    setNextId(1);
  };

  const updateCandidate = (id: number, updates: Partial<Omit<Candidate, "id">>) => {
    setCandidates(candidates.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const isAddButtonDisabled = candidates.length >= MAX_CANDIDATE_COUNT;

  return (
    <>
      {candidates.map((candidate, index) => {
        const isFirstCandidate = index === 0;
        const candidateNumber = index + 1;
        const dayFieldName = `${formNameBase}[${candidateNumber}][day]`;
        const timesFieldName = `${formNameBase}[${candidateNumber}][times]`;

        return (
          <PreferredDayAndTimeItem
            key={candidate.id}
            candidateNumber={candidateNumber}
            dateRange={dateRange}
            dayFieldName={dayFieldName}
            timesFieldName={timesFieldName}
            selectedDate={candidate.selectedDate}
            selectedTimeOption={candidate.timeOption}
            selectedTimeSlots={candidate.selectedTimeSlots}
            timeOptions={TIME_TYPE_OPTIONS.map((opt) => ({
              ...opt,
              id: `user-hoped-interview-time-option-${candidateNumber}-${opt.value}`,
            }))}
            timeSlotChoices={availableTimeSlots.map((slot) => ({
              id: `time-slot-${candidateNumber}-${slot.value}`,
              label: slot.labelName,
              value: slot.value,
            }))}
            allDayValue={allDayValue}
            showTimeSlots={candidate.timeOption === TIME_OPTION.TIME_RANGE}
            hasError={!!errors[dayFieldName] || !!errors[timesFieldName]}
            dayErrorText={errors[dayFieldName]?.join(", ")}
            timesErrorText={errors[timesFieldName]?.join(", ")}
            buttonText={isFirstCandidate ? "リセット" : "削除"}
            onDateChange={(date) => updateCandidate(candidate.id, { selectedDate: date })}
            onTimeOptionChange={(value) => updateCandidate(candidate.id, { timeOption: value })}
            onTimeSlotsChange={(slots) =>
              updateCandidate(candidate.id, { selectedTimeSlots: slots })
            }
            onButtonClick={
              isFirstCandidate ? handleReset : () => handleDeleteCandidate(candidate.id)
            }
          />
        );
      })}

      <div className={styles.buttonWrapper}>
        <Button
          size="large"
          theme={isAddButtonDisabled ? "disabled" : "secondary"}
          onClick={handleAddCandidate}
          disabled={isAddButtonDisabled}
          className={styles.addButton}
          leftIcon={<Icon name="add-more" fontSize={fontSize["fs-16"]} />}
        >
          日時を追加する
        </Button>
        {!isAddButtonDisabled && (
          <PopOver
            arrowPosition="topCenter"
            left="50%"
            right="auto"
            top="41px"
            className={styles.popOver}
          >
            複数入力すると面接までスムーズです！
          </PopOver>
        )}
      </div>
    </>
  );
};
